"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "@/lib/motion";
import type { IpInfo } from "@suite/lib/geo";
import { GEO_DATA_ATTRIBUTION } from "@/lib/constants";
import {
  assessLeak,
  canonicalizeIp,
  localNetworkNote,
  runWebRtcProbe,
  PROBE_SLOW_AFTER_MS,
  STUN_SERVERS,
  type AddressKind,
  type CandidateType,
  type ComparedAddress,
  type IceCandidateRecord,
  type IpFamily,
  type LeakAssessment,
  type LeakVerdictKind,
  type WebRtcProbeResult,
} from "@suite/lib/webrtc";
import { readCapturePermission, type CapturePermission } from "@suite/lib/media-permission";
import { Button } from "@/components/ui/button";
import { Card } from "@suite/components/ui/card";
import {
  AlertCircle,
  Check,
  Download,
  Globe,
  Info,
  RefreshCw,
  Waypoints,
} from "@suite/components/icons";
import { cn, countryToFlag } from "@suite/lib/utils";
import CopyButton from "./CopyButton";
import IceCandidateRow, { CANDIDATE_TYPE_HINTS } from "./IceCandidateRow";
import WebRtcScanning, { type ScanPhase } from "./WebRtcScanning";

/* The WebRTC Leak Test, built around one idea: the whole tool is a single comparison, so the
 * page IS that comparison. One split panel holds the address sites see and the address WebRTC
 * reveals, separated by a single shared hairline; the verdict lives in the headline and the
 * status chip, never in a widget between the two readings. One plain sentence below, the
 * local network in a single line, and the full candidate log parked in its own card for
 * whoever wants the receipts. Nothing else competes for attention. */

const HEADER = "font-mono text-[9px] font-semibold uppercase tracking-widest text-white/70";

/**
 * Dev-only: what a settled probe looks like once a slow interface finally answers with a DIFFERENT
 * public address, ~9s in. Drives the `?demo=revised` preview through the same `publish` path a
 * real late candidate takes.
 */
function demoLateProbe(probe: WebRtcProbeResult): WebRtcProbeResult {
  const address = "1.1.1.1";
  return {
    ...probe,
    status: "settled",
    endOfCandidates: false,
    serverAnswered: true,
    durationMs: 9200,
    candidates: [
      ...probe.candidates,
      {
        atMs: 9200,
        type: "srflx",
        protocol: "udp",
        address,
        port: 3478,
        relatedAddress: null,
        raw: `candidate:demo 1 udp 1685987071 ${address} 3478 typ srflx generation 0 (demo late)`,
      },
    ],
    addresses: [
      ...probe.addresses,
      { address, family: "ipv4", kind: "public", sources: ["srflx"], firstSeenMs: 9200 },
    ],
  };
}

/**
 * What a revision did, in one sentence. Shared by the visible strip and the live region: a result
 * that rewrites itself is exactly the thing a screen-reader user cannot see happen, so the two
 * must say the same thing. Without this in the announcement, a revision that kept the same kind
 * produced a byte-identical string and was announced not at all.
 */
function revisionSentence(revision: { kindChanged: boolean; atSeconds: number }): string {
  return revision.kindChanged
    ? `This result changed. A network path arrived ${revision.atSeconds} seconds in, after the first answer, and the verdict above now includes it.`
    : `One more network path arrived ${revision.atSeconds} seconds in, after the first answer. It didn't change the verdict; it's listed above and in the log.`;
}

/** Dev-only: the `?demo=` value, or null in production builds and on the server. */
function demoMode(): string | null {
  if (process.env.NODE_ENV === "production" || typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("demo");
}

/**
 * Dev-only previews of every verdict presentation, driven by `?demo=<state>`. Each case doctors
 * only the finished probe (or, for `nobaseline`, the connection read in `run`) and lets the
 * real pipeline — geo lookup, ASN comparison, digit diff, verdict ladder — produce the render,
 * so what shows is exactly what the genuine condition shows. Compiled out of production builds.
 *
 *   leak         red ×   public address on another network (8.8.8.8, resolves to Google's AS)
 *   unverified   amber × public address whose geo lookup fails (2001:30::1 — classified public
 *                        by design, but an IANA special-purpose block no geo DB carries)
 *   local        !       raw private LAN address handed out unmasked
 *   cgnat        !       raw CGNAT address handed out unmasked
 *   settled      =       the normal tunnel-interface ending: a server answered, the browser
 *                        never signalled the end, and the run settled on the quiet window
 *   timeout      …       gathering cut short with nothing to show for it
 *   unanswered   …       cut short having produced nothing at all (the rewritten `incomplete`)
 *   empty        ∅       probe finished, no public address surfaced
 *   silent       ×       probe finished with no candidates at all
 *   stun         ?       candidates arrived but no STUN server ever answered
 *   unsupported  ×       no WebRTC to call
 *   nobaseline   ?       connection read failed, nothing to compare against
 *
 * Two are handled in `run` rather than here, because they are about the run's TIMING and there is
 * no finished probe to doctor: `slow` never publishes, so the scanning frame's slow state stays on
 * screen to be looked at; `revised` publishes normally and then feeds a synthetic late address
 * through the real `onLate` path, so the revision strip is exercised end to end rather than faked.
 *
 * Replacing the public addresses (rather than appending) renders the proxy-bypass shape:
 * WebRTC's UDP never touches an HTTP proxy, so a proxied browser leaks exactly one address.
 */
function applyDemo(probe: WebRtcProbeResult): WebRtcProbeResult {
  const demo = demoMode();
  if (!demo) return probe;
  const publicAddresses = new Set(
    probe.addresses.filter((a) => a.kind === "public").map((a) => a.address)
  );
  const withoutPublic: WebRtcProbeResult = {
    ...probe,
    candidates: probe.candidates.filter((c) => !publicAddresses.has(c.address)),
    addresses: probe.addresses.filter((a) => a.kind !== "public"),
  };
  const fake = (
    base: WebRtcProbeResult,
    address: string,
    type: CandidateType,
    family: IpFamily,
    kind: AddressKind
  ): WebRtcProbeResult => ({
    ...base,
    candidates: [
      ...base.candidates,
      {
        atMs: probe.durationMs,
        type,
        protocol: "udp",
        address,
        port: 3478,
        relatedAddress: null,
        raw: `candidate:demo 1 udp 1685987071 ${address} 3478 typ ${type} generation 0 (demo)`,
      },
    ],
    addresses: [
      ...base.addresses,
      { address, family, kind, sources: [type], firstSeenMs: probe.durationMs },
    ],
  });
  switch (demo) {
    case "leak":
      return fake(withoutPublic, "8.8.8.8", "srflx", "ipv4", "public");
    case "unverified":
      return fake(withoutPublic, "2001:30::1", "srflx", "ipv6", "public");
    case "local":
      return fake(probe, "192.168.1.23", "host", "ipv4", "private");
    case "cgnat":
      return fake(probe, "100.64.0.5", "host", "ipv4", "cgnat");
    case "settled":
      return { ...probe, status: "settled", endOfCandidates: false, serverAnswered: true };
    case "timeout":
      return { ...probe, status: "timeout", endOfCandidates: false };
    case "unanswered":
      return {
        ...probe,
        status: "timeout",
        endOfCandidates: false,
        serverAnswered: false,
        candidates: [],
        addresses: [],
        stunUnreachable: false,
        stunErrors: [],
      };
    case "empty":
      return withoutPublic;
    case "silent":
      return { ...probe, candidates: [], addresses: [], stunUnreachable: false, stunErrors: [] };
    case "stun":
      return { ...withoutPublic, stunUnreachable: true };
    case "unsupported":
      return {
        supported: false,
        endOfCandidates: false,
        status: "unsupported",
        serverAnswered: false,
        durationMs: 0,
        candidates: [],
        addresses: [],
        stunServers: probe.stunServers,
        stunUnreachable: false,
        stunErrors: [],
      };
    default:
      return probe;
  }
}

/** The two reading labels: an icon seated in the house green chip (the same language as the
 * fingerprint tool's category headers) beside a larger, brighter mono label, so each half of
 * the comparison announces itself instead of whispering. */
const READING_LABEL = "font-mono text-[11px] font-semibold uppercase tracking-widest text-white/85";
const READING_ICON =
  "bg-brand-green/10 text-brand-green flex size-6 shrink-0 items-center justify-center rounded-md";

/**
 * The run-over-run chip, in the footer row's own microtype.
 *
 * It used to be 11px sans in a pill, sitting in a row of 9px mono — three type scales in one
 * line. Every box was centred correctly, but the baselines could not agree, so the chip read as
 * dropped out of the row. Same size and family as its neighbours, it reads as part of them.
 *
 * Sentence case, unlike the uppercase microtype beside it: this is the one item in the row that
 * is a sentence rather than a label, and setting it in caps made the only good news on the card
 * shout over the readings it comments on.
 */
const TRANSITION_CHIP =
  "inline-flex w-fit items-center gap-1.5 rounded-full px-2 py-1 font-mono text-[9px] font-medium";

/** In-page jump control: mono text between terminal brackets, shared with the fingerprint
 * tool. Touch target lifted to ~24px without moving the layout. */
const JUMP_LINK =
  "focus-visible:ring-brand-green/70 text-brand-green before:text-brand-green/40 after:text-brand-green/40 hover:before:text-brand-green hover:after:text-brand-green -my-1.5 inline-flex w-fit items-center gap-1.5 rounded-sm py-1.5 font-mono text-[10px] font-semibold tracking-widest uppercase transition-colors outline-none before:content-['['] after:content-[']'] hover:text-white focus-visible:ring-2";

/** Bubble chrome for chip explanations: one plain wrapping sentence. */
const TIP_SENTENCE =
  "rounded-md bg-black px-2.5 py-2 font-sans text-[11px] leading-relaxed font-normal tracking-normal text-white/80 normal-case ring-1 ring-white/25";

type VerdictTone = "good" | "warn" | "bad";

const TONE_TEXT: Record<VerdictTone, string> = {
  good: "text-brand-green",
  warn: "text-amber-300",
  bad: "text-red-300",
};

/** One flowing sentence per verdict, in the fingerprint tool's register: white words carrying
 * the verdict phrase in the verdict's colour. Every headline states only what the probe
 * proved — a run that timed out or couldn't be verified says so instead of guessing, and only
 * a COMPLETED gathering may claim a pass. The nuance lives in the gloss, tooltips and log. */
const VERDICTS: Record<
  LeakVerdictKind,
  { pre: string; em: string; tone: VerdictTone; gloss: string }
> = {
  leak: {
    pre: "WebRTC ",
    em: "exposed a different IP address",
    tone: "bad",
    gloss:
      "It handed sites an address your connection isn't using. If that's your normal ISP address, WebRTC went around your proxy or VPN.",
  },
  "second-address": {
    pre: "WebRTC ",
    em: "exposed a second address",
    tone: "warn",
    gloss:
      "The extra address is your connection's other address family, the way a normal dual-stack line looks. Websites can already reach you at it, so nothing escaped that was hidden, but they do learn one more address than the first one showed.",
  },
  "extra-unverified": {
    pre: "WebRTC ",
    em: "exposed another address",
    tone: "warn",
    gloss:
      "We couldn't find out whose network that address belongs to. Check whether it's your proxy or VPN; if it isn't, treat it as a leak.",
  },
  "exposed-unverified": {
    pre: "This result ",
    em: "couldn't be verified",
    tone: "warn",
    gloss:
      "We couldn't fetch your connection's address to compare against. If the WebRTC address shown isn't your proxy or VPN, it's leaking.",
  },
  "route-changed": {
    pre: "Your exit address ",
    em: "changed during the test",
    tone: "warn",
    gloss:
      "Your connection left from one address at the start and a different one at the end, which a rotating proxy will do. That makes a mismatch impossible to judge, so re-run for a clean comparison.",
  },
  "route-unverified": {
    pre: "We ",
    em: "couldn't confirm your exit address",
    tone: "warn",
    gloss:
      "An extra address turned up, but re-checking the address your connection uses failed, so we can't tell a rotating proxy apart from a bypass. Re-run for a clean comparison.",
  },
  "stun-unreachable": {
    pre: "The test ",
    em: "couldn't check for a public address",
    tone: "warn",
    gloss:
      "The servers that reveal your public address never answered, so this run can't tell you whether WebRTC would expose one. A firewall, a DNS filter, or a network that drops UDP will do this. Check your network and re-run.",
  },
  match: {
    pre: "This browser ",
    em: "passed the WebRTC leak test",
    tone: "good",
    gloss: "WebRTC shows only the address sites already see.",
  },
  "no-public": {
    pre: "This browser ",
    em: "passed the WebRTC leak test",
    tone: "good",
    gloss: "WebRTC finished gathering without revealing any public address.",
  },
  "local-exposed": {
    pre: "WebRTC ",
    em: "exposed your local network address",
    tone: "warn",
    gloss:
      "No public address escaped, but your browser handed out its address on your own network. Nobody can reach you at it, and any script can still read it as a way to recognise your device.",
  },
  /* Kept apart from local-exposed so the copy can be true. 100.64.0.0/10 is shared CGNAT space,
   * which is also where Tailscale lives — calling it "your own network" and pointing the visitor
   * at their browser settings would be wrong twice over, and wrong at mobile-proxy users
   * specifically. It says nothing about a LAN, so the copy claims nothing about one.
   *
   * The headline names the RANGE rather than guessing an owner, for the same reason: "a mobile
   * operator's NAT" is false for a Tailscale address and "your ISP's" is false for both. The
   * gloss below carries who actually uses the range. */
  "cgnat-exposed": {
    pre: "WebRTC ",
    em: "exposed an address from a shared range",
    tone: "warn",
    gloss:
      "No public address escaped. The address your browser handed out belongs to the shared range mobile networks and Tailscale use, so it is not your home network and nobody can reach you at it. A script can still read it as one more detail about your connection.",
  },
  unreadable: {
    pre: "The test ",
    em: "found something it couldn't read",
    tone: "warn",
    gloss:
      "Your browser offered a network path in a form we don't recognise, so we can't say whether it identifies you. The candidate log below has the raw value.",
  },
  /* Reachable only when the run produced nothing to judge AND never signalled that it had
   * finished. It used to sit above every finding in the ladder, which meant a cut-short run that
   * HAD found something threw the finding away and said this instead. Now that it means one thing,
   * the copy can name that thing. */
  incomplete: {
    pre: "The test ",
    em: "got no answer from your browser",
    tone: "warn",
    gloss:
      "Your browser offered no usable network path and never signalled that it had finished looking, so this run can't tell you either way. An extension that blocks WebRTC, or a network that drops its traffic, will do this. Re-run to try again.",
  },
  silent: {
    pre: "WebRTC is ",
    em: "switched off in this browser",
    tone: "good",
    gloss:
      "Your browser offered no WebRTC connections at all, so a site running this probe learns nothing about you. A privacy extension or a browser setting is what usually does this, including the WebRTC limiter in the Proxidize extension.",
  },
  unsupported: {
    pre: "This browser ",
    em: "doesn't support WebRTC",
    tone: "good",
    gloss: "This browser has no peer connection API, so WebRTC can't reveal anything.",
  },
};

/**
 * Optical centring for the badge glyph, because flexbox cannot do it.
 *
 * `items-center` centres the LINE BOX, a font metric, not the shape you can see. Measured on a
 * canvas at the badge's own font (bold 20px Geist Mono), `…` sits 4.35px BELOW where `=` sits,
 * because an ellipsis rests on the baseline — correct typography, wrong for the middle of a
 * 40px disc. `?` and `!` are ~1.3px high for the same reason in reverse.
 *
 * `=` is the reference: the disc was designed around it, so the others are nudged to match ITS
 * optical centre rather than an absolute ideal. Values are the measured delta over `=`, in em,
 * so they survive a change of badge size.
 *
 * To re-derive after a font change:
 *   ctx.font = 'bold 20px "Geist Mono"';
 *   const m = ctx.measureText(g);   // centre = -(m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2
 * then take each glyph's centre minus the centre of `=`, divided by the font size.
 */
const GLYPH_NUDGE_EM: Record<string, number> = {
  "…": -0.218,
  "?": 0.07,
  "!": 0.062,
};

function glyphNudge(glyph: string): { transform: string } | undefined {
  const em = GLYPH_NUDGE_EM[glyph];
  return em ? { transform: `translateY(${em}em)` } : undefined;
}

/**
 * The badge's glyph: `=` the two readings agree, `×` something extra surfaced, `?` nothing to
 * compare against, `…` the run was cut short, `∅` empty result, `!` a local address was handed
 * out.
 *
 * `×` does double duty — it also marks a browser with WebRTC switched off or absent, which is a
 * GOOD result. The two are told apart by the disc's colour and, decisively, by the headline
 * beside it, which always states the verdict in words; the badge is `aria-hidden` and adds no
 * information of its own. It replaced a composed `≠` (Geist Mono has no U+2260, so the character
 * had to be drawn as `=` plus a rotated slash to avoid the browser substituting another typeface
 * mid-word) purely because the mark read poorly at 20px.
 */
const GLYPHS: Record<LeakVerdictKind, string> = {
  match: "=",
  leak: "×",
  "second-address": "×",
  "extra-unverified": "×",
  "exposed-unverified": "?",
  "route-changed": "?",
  "route-unverified": "?",
  "stun-unreachable": "?",
  unreadable: "?",
  "local-exposed": "!",
  "cgnat-exposed": "!",
  incomplete: "…",
  "no-public": "∅",
  silent: "×",
  unsupported: "×",
};

/** Why the local-network reading came out as it did, in terms of the one thing that controls
 * it: whether this origin holds camera or microphone access.
 *
 * The masked case gets a clause appended to the tooltip that line already carries, on a hard
 * length budget — that bubble is w-64 at 11px with no collision detection, and the tip it joins
 * is already 24 words. The exposed cases get a plain visible sentence instead, because they are
 * the only ones a visitor can act on and a hover target is no place to put an instruction. */
const MASKED_TIP_CLAUSE = "Browsers only reveal it to sites you've given camera or mic access.";

/** The same fact, self-contained, for the exports and the live region. The clause above leans on
 * the sentence it is appended to for its "it"; a downloaded report has no such antecedent. */
const MASKED_NOTE_FULL =
  "Your local address stayed hidden because browsers only reveal it to sites you've given camera or microphone access, and you've given this page neither.";

const EXPOSED_NOTES: Record<"exposed-with-capture" | "exposed-otherwise", string> = {
  "exposed-with-capture":
    "You've given this page camera or microphone access, and that switches the hiding off. Revoke it in your browser's site settings and re-run.",
  /* States the observation and offers candidates; it must not assert that masking was switched
   * off. The same run can also contain a `.local` name (a patched or anti-detect browser will do
   * this, and exposure outranks masking in the ordering), and "something else switched it off"
   * would then be contradicted by the tool's own evidence — sending the visitor hunting a flag
   * that is not set. */
  "exposed-otherwise":
    "You haven't given this page camera or microphone access, so this address should have been hidden. A browser flag, an extension, or a setting your workplace controls can switch that hiding off.",
};

/** The verdict badge: a solid disc punched through the seam between the two readings, the way
 * a currency-swap button sits between two amount fields. Filled with the verdict's colour, a
 * dark glyph for contrast, socketed into the divider by a near-black ring, with a machined
 * top highlight so it reads as a physical key rather than a flat sticker. Its column carries
 * its own clearance (sm:px-8), and the readings retreat to the plaque's outer edges, so the
 * disc can never sit on top of an address. */
const BADGE_FILL: Record<VerdictTone, string> = {
  good: "bg-brand-green text-black",
  warn: "bg-amber-300 text-black",
  bad: "bg-red-400 text-black",
};

/** One sonar ping in the verdict's colour when the answer lands: a single ring expands from
 * the badge and dies. Once per run, never looping. */
const BADGE_RIPPLE: Record<VerdictTone, string> = {
  good: "bg-brand-green",
  warn: "bg-amber-300",
  bad: "bg-red-400",
};

// --- Small parts ---------------------------------------------------------------------------

interface HoverTipProps {
  tip: string;
  bubbleClassName: string;
  as?: "li" | "div" | "span";
  className?: string;
  children?: React.ReactNode;
}

/** Hover/focus tooltip meeting WCAG 1.4.13: keyboard-focusable trigger, Escape dismisses without
 * moving focus, bubble reachable by pointer. Screen readers get the sr-only twin. */
function HoverTip({ tip, bubbleClassName, as: Tag = "div", className, children }: HoverTipProps) {
  const [dismissed, setDismissed] = useState(false);
  const [hovered, setHovered] = useState(false);

  // Escape is bound on the DOCUMENT, not the trigger. A trigger-level handler only fires when the
  // trigger has focus, so a pointer user hovering the tip had no way to dismiss it — which is the
  // Dismissible condition of 1.4.13 unmet, on a component whose own comment claimed compliance.
  useEffect(() => {
    if (!hovered) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDismissed(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [hovered]);

  return (
    <Tag
      tabIndex={0}
      className={cn(
        "group/tip focus-visible:ring-brand-green/70 rounded-sm outline-none focus-visible:ring-2",
        className
      )}
      onKeyDown={(e) => {
        if (e.key === "Escape") setDismissed(true);
      }}
      onMouseEnter={() => setHovered(true)}
      onFocus={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setDismissed(false);
      }}
      onBlur={() => {
        setHovered(false);
        setDismissed(false);
      }}
    >
      {children}
      <span className="sr-only">{tip}</span>
      <span
        aria-hidden
        className={cn(
          "absolute bottom-full z-10 pb-1.5",
          dismissed ? "hidden" : "hidden group-hover/tip:block group-focus/tip:block",
          bubbleClassName
        )}
      >
        <span className={cn("block", TIP_SENTENCE)}>{tip}</span>
      </span>
    </Tag>
  );
}

interface DiffAddressProps {
  address: string;
  /** The connection address the segments are compared against. */
  baseline: string;
  tone: "warn" | "bad";
}

/** The address rendered as a diff against the baseline: segments that match stay quiet,
 * segments that differ light up in the verdict's colour. On a leak the eye sees exactly
 * where the two addresses diverge, digit by digit, the way a code diff reads. */
function DiffAddress({ address, baseline, tone }: DiffAddressProps) {
  const sep = address.includes(":") && baseline.includes(":") ? ":" : ".";
  if (!address.includes(sep) || !baseline.includes(sep)) return <>{address}</>;
  const own = address.split(sep);
  const base = baseline.split(sep);
  const segments = own.map((text, position) => ({
    id: `${position}-${text}`,
    text,
    differs: base[position] !== text,
    last: position === own.length - 1,
  }));
  return (
    <>
      {segments.map((segment) => (
        <span key={segment.id}>
          <span
            className={
              segment.differs ? (tone === "bad" ? "text-red-300" : "text-amber-300") : undefined
            }
          >
            {segment.text}
          </span>
          {!segment.last && <span className="text-white/45">{sep}</span>}
        </span>
      ))}
    </>
  );
}

/** "flag Country · ISP", from whatever fields the lookup actually returned. */
function geoLine(info: IpInfo | undefined): string | null {
  if (!info) return null;
  const flag = info.countryCode ? `${countryToFlag(info.countryCode)} ` : "";
  const place = info.country ? `${flag}${info.country}` : null;
  return [place, info.isp].filter(Boolean).join(" · ") || null;
}

function isIpInfo(value: unknown): value is IpInfo {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return typeof record.ip === "string" && (record.ipVersion === "v4" || record.ipVersion === "v6");
}

/**
 * The connection baseline. Bounded, because it is awaited inside the same `Promise.all` as the
 * probe and the probe is the only half of that race that was ever given deadlines: an upstream
 * that black-holes the request rather than resetting it (captive portal, dead mobile line) used
 * to pin the tool on the scanning skeleton indefinitely, with no verdict, no timeout, and a
 * permanently disabled Re-run. The 3s soft-partial does not cover it either, since a normal run
 * finishes gathering in well under a second and clears that timer before it can fire.
 *
 * Failing here is already a defined outcome — a null baseline degrades to a verdict that says
 * the comparison could not be made — so a deadline converts a hang into an honest answer. Set
 * comfortably above a slow mobile round trip but below the probe's own hard cap, so this can
 * never be the thing the run is waiting on.
 */
const BASELINE_TIMEOUT_MS = 10_000;

async function fetchIpInfo(url: string): Promise<IpInfo | null> {
  try {
    const response = await fetch(url, {
      cache: "no-store",
      signal: AbortSignal.timeout(BASELINE_TIMEOUT_MS),
    });
    if (!response.ok) return null;
    const value: unknown = await response.json();
    return isIpInfo(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * Endpoints pinned to one address family, used to learn the visitor's exit address in the family
 * their normal connection did NOT use. With both exits known, a cross-family extra address is
 * settled by exact equality rather than inferred from a shared AS number — which takes the
 * network lookup out of the verdict entirely.
 *
 * Both are optional. Unset (or unreachable, which is the norm for an IPv4-only visitor asked for
 * IPv6) simply leaves the verdict on its existing ASN path, so this degrades to the previous
 * behaviour rather than failing. They must be hostnames that resolve in ONE family only, which
 * is a DNS/hosting matter rather than something this app can arrange for itself.
 */
const FAMILY_ENDPOINTS: { family: IpFamily; url: string | undefined }[] = [
  { family: "ipv4", url: process.env.NEXT_PUBLIC_IPV4_ECHO_URL },
  { family: "ipv6", url: process.env.NEXT_PUBLIC_IPV6_ECHO_URL },
];

/** A family-pinned endpoint is a best-effort extra: a short timeout keeps an unroutable family
 * (no IPv6 on this line) from holding up the verdict. */
async function fetchFamilyBaseline(url: string): Promise<IpInfo | null> {
  try {
    const response = await fetch(url, { cache: "no-store", signal: AbortSignal.timeout(3000) });
    if (!response.ok) return null;
    const value: unknown = await response.json();
    return isIpInfo(value) ? value : null;
  } catch {
    return null;
  }
}

/** The visitor's exit address in the family their main request did not use, if we can get it. */
async function fetchOtherFamilyBaseline(primary: IpFamily): Promise<IpInfo | null> {
  const target = FAMILY_ENDPOINTS.find((e) => e.family !== primary && e.url);
  return target?.url ? fetchFamilyBaseline(target.url) : null;
}

/** At most this many distinct addresses get a network lookup per run. The upstream provider is
 * a shared free tier, so an unbounded fan-out would rate-limit itself and turn every answer
 * into "couldn't verify". Anything past the cap stays explicitly unverified instead. */
const LOOKUP_CAP = 12;
/** Concurrent lookups. Enough to stay fast on a normal run of one or two addresses without
 * bursting the provider when a machine surfaces many interfaces. */
const LOOKUP_CONCURRENCY = 4;

/** Run `work` over `items` with a fixed number of workers, so a long list can't burst. */
async function mapLimited<T>(items: readonly T[], limit: number, work: (item: T) => Promise<void>) {
  let cursor = 0;
  const worker = async () => {
    while (cursor < items.length) {
      const index = cursor++;
      await work(items[index]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}

/** Geo/ASN per address, keyed by CANONICAL address so a second spelling of one address reads
 * back the answer already fetched for it instead of coming up empty. */
async function enrichAddresses(
  connection: IpInfo | null,
  addresses: readonly { address: string }[]
): Promise<Record<string, IpInfo>> {
  // The baseline is inserted FIRST so the cap can never evict it. Its ASN is the reference every
  // comparison is made against; lose it and every cross-family extra degrades to "unverified".
  const byCanonical = new Map<string, string>();
  if (connection) byCanonical.set(canonicalizeIp(connection.ip), connection.ip);
  for (const { address } of addresses) {
    const key = canonicalizeIp(address);
    if (!byCanonical.has(key)) byCanonical.set(key, address);
  }

  const geo: Record<string, IpInfo> = {};
  // The CANONICAL form is what gets looked up, never the spelling the browser happened to emit.
  // The database reader does not parse addresses the way this app does, so a raw literal can
  // return the record for a DIFFERENT address — and since the AS number decides the dual-stack
  // question, a wrong record flips a benign second address to a red leak or the reverse.
  await mapLimited(
    [...byCanonical.keys()].slice(0, LOOKUP_CAP),
    LOOKUP_CONCURRENCY,
    async (key) => {
      const info = await fetchIpInfo(`/api/tools/ip/?ip=${encodeURIComponent(key)}`);
      if (info) geo[key] = info;
    }
  );
  return geo;
}

/** Read geo for an address regardless of how it happens to be spelled. */
function geoFor(geo: Record<string, IpInfo>, address: string | undefined): IpInfo | undefined {
  return address ? geo[canonicalizeIp(address)] : undefined;
}

function assessWithGeo(
  probe: WebRtcProbeResult,
  connection: IpInfo | null,
  geo: Record<string, IpInfo>,
  recheck?: { attempted: boolean; ip: string | null },
  otherFamily?: IpInfo | null
): LeakAssessment {
  const asnByAddress: Record<string, string | undefined> = {};
  for (const address of probe.addresses) {
    if (address.kind !== "public") continue;
    const key = canonicalizeIp(address.address);
    asnByAddress[key] = geo[key]?.asn;
  }
  return assessLeak(
    probe,
    connection
      ? {
          ip: connection.ip,
          asn: connection.asn,
          otherFamily: otherFamily ? { ip: otherFamily.ip, asn: otherFamily.asn } : null,
        }
      : null,
    asnByAddress,
    recheck
  );
}

// --- Report state ---------------------------------------------------------------------------

interface ResultState {
  probe: WebRtcProbeResult;
  /** The address the visitor's HTTP traffic exits from, i.e. their proxy/VPN when active. */
  connection: IpInfo | null;
  /** Geo per WebRTC-surfaced public address. */
  geo: Record<string, IpInfo>;
  assessment: LeakAssessment;
  /**
   * Whether the location lookup has finished, as distinct from having found nothing.
   *
   * The verdict publishes before it: the baseline endpoint answers with the address alone, no
   * country or ISP, because that is the fast path the whole sub-second answer depends on. Without
   * this flag the location line said "Location unavailable" during the gap and then corrected
   * itself, which reads as the tool getting it wrong and then changing its mind — and it was
   * claiming a lookup had failed before one had been made.
   */
  geoSettled: boolean;
}

/** How this run relates to the previous one. Re-running after a settings change is the tool's
 * core workflow, so the delta gets first-class treatment. */
type RunTransition = "closed" | "opened" | "same" | "changed";

const EXPOSED_KINDS = new Set([
  "leak",
  "second-address",
  "exposed-unverified",
  "extra-unverified",
  "local-exposed",
  "cgnat-exposed",
]);

/** A run that proved nothing never participates in run-over-run comparison: it neither
 * announces "exposure closed" nor becomes the baseline the next run compares against. A probe
 * that timed out, errored on construction, hit a rotating exit address, or produced something
 * unreadable all fall here — none of them measured the thing the badge would be claiming. */
const INCONCLUSIVE_KINDS = new Set([
  "incomplete",
  "route-changed",
  "route-unverified",
  "stun-unreachable",
  "unreadable",
  "exposed-unverified",
]);

/** The exact state the UI shows, included in both exports so they can never disagree with the
 * screen. */
interface PresentedResult extends ResultState {
  presentation: {
    verdict: string;
    run: number;
    comparisons: number;
    transition: RunTransition | null;
    /** The resolved sentence, not the enum: a downloaded audit file has to explain itself to
     * someone who never saw the screen. Null when there was nothing honest to say. */
    localNetworkNote: string | null;
  };
  /** The country/ISP/AS values in this export are derived from a CC-BY-4.0 database, and that
   * licence asks for the credit wherever the data is shared. A downloaded file is shared. */
  attribution: {
    ipData: string;
    source: string;
    license: string;
    licenseUrl: string;
  };
}

type AddressStatus = "matches" | "sameNetwork" | "escaped" | "unverified";

const STATUS_WORDS: Record<AddressStatus, string> = {
  matches: "matches",
  sameNetwork: "same network",
  escaped: "leaked",
  unverified: "unverified",
};

/** Colour REINFORCES the word above; it never carries the distinction alone. The panel used to
 * separate "matches" from "leaked" with nothing but a red tint on the digits that differ, which
 * is the tool's single most important output resting entirely on hue — unreadable to anyone who
 * cannot see the difference, and invisible in a greyscale screenshot. The candidate log already
 * states this rule for itself ("the kind word next to it says the same thing in text so the
 * state is never colour-only"); this is the same rule, applied where it matters most.
 *
 * `matches` is present for completeness but never rendered: a matching address carries no colour
 * to caption, and the headline and badge already say it passed. The rule is about states that
 * flag something, not about labelling every row. */
const STATUS_TONE: Record<AddressStatus, string> = {
  matches: "text-brand-green",
  sameNetwork: "text-amber-300",
  escaped: "text-red-300",
  unverified: "text-amber-300",
};

/** Straight from the assessment's own partition, so the word next to an address can never
 * disagree with the headline built from the same partition. */
function addressStatus(c: ComparedAddress): AddressStatus {
  if (c.disposition === "matches") return "matches";
  if (c.disposition === "second-address") return "sameNetwork";
  if (c.disposition === "leaked") return "escaped";
  return "unverified";
}

function reportToText(state: PresentedResult): string {
  const { probe, connection, geo, assessment, presentation } = state;
  const lines: string[] = [
    "WebRTC Leak Test",
    `Verdict: ${presentation.verdict}`,
    `Scope: this browser, this session. Re-run after changing proxy, VPN or browser settings.`,
    "",
  ];
  if (connection) {
    const g = geoLine(connection);
    lines.push(`What sites see (connection exit): ${connection.ip}${g ? ` (${g})` : ""}`);
  } else {
    lines.push("What sites see: unreachable, so WebRTC addresses could not be compared.");
  }
  lines.push("", `What WebRTC reveals (${assessment.compared.length} public):`);
  if (assessment.compared.length === 0) {
    lines.push("  none");
  } else {
    for (const c of assessment.compared) {
      const status = STATUS_WORDS[addressStatus(c)];
      const g = geoLine(geoFor(geo, c.address));
      lines.push(
        `  ${c.address} [${c.family}, via ${c.sources.join("/")}]: ${status}${g ? ` (${g})` : ""}`
      );
    }
  }
  const local = probe.addresses.filter((a) => a.kind !== "public");
  lines.push("", `Local and masked addresses (${local.length}):`);
  lines.push(local.length ? local.map((a) => `  ${a.address} [${a.kind}]`).join("\n") : "  none");
  if (presentation.localNetworkNote) lines.push(`  ${presentation.localNetworkNote}`);
  lines.push("", `Network paths (${probe.candidates.length}):`);
  for (const c of probe.candidates) {
    lines.push(
      `  +${(c.atMs / 1000).toFixed(2)}s ${c.type} ${c.protocol} ${c.address}${
        c.port !== null ? `:${c.port}` : ""
      }`
    );
  }
  lines.push(
    "",
    `Measurement ${state.assessment.measurementComplete ? "complete" : "INCOMPLETE — more addresses may be missing"}`,
    `Gathering ${
      probe.status === "complete"
        ? "completed"
        : probe.status === "settled"
          ? "settled (a STUN server had answered and no new path arrived; your browser never signalled the end of gathering, which is normal with a VPN or tunnel interface up, and we kept listening afterwards)"
          : probe.status === "timeout"
            ? "was cut off at the timeout"
            : probe.status === "errored"
              ? "could not start (the peer connection threw)"
              : `did not complete (${probe.status})`
    } in ${(probe.durationMs / 1000).toFixed(1)}s`,
    `STUN servers: ${probe.stunServers.join(", ")}`,
    `Note: an extra address in your line's OTHER address family is judged against your own exit`,
    `in that family when we know it, and by AS number when we don't. A same-family extra address`,
    `is never explained away as dual stack, and anything we could not check stays unverified`,
    `rather than safe. A matching result means WebRTC surfaced nothing beyond what websites`,
    `already see; it does not certify the connection itself is private.`,
    "",
    // CC-BY-4.0 requires the credit wherever the data is shared, and this report carries country,
    // ISP and AS number derived from it.
    `${GEO_DATA_ATTRIBUTION.text} (${GEO_DATA_ATTRIBUTION.href}), licensed ${GEO_DATA_ATTRIBUTION.licenseText} (${GEO_DATA_ATTRIBUTION.licenseHref})`
  );
  return lines.join("\n");
}

// --- Main component --------------------------------------------------------------------------

export default function WebRtcLeakTest() {
  const [result, setResult] = useState<ResultState | null>(null);
  const [busy, setBusy] = useState(true);
  const [liveCandidates, setLiveCandidates] = useState<IceCandidateRecord[]>([]);
  const [phase, setPhase] = useState<ScanPhase>("probing");
  /** Ticks only while the wait is long enough to narrate, so the common sub-second run never
   * schedules an interval at all. */
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [runId, setRunId] = useState(0);
  const [comparisons, setComparisons] = useState(0);
  const [transition, setTransition] = useState<RunTransition | null>(null);
  /** Set when a path arrived after the verdict was published, so the card can say the result
   * changed under the visitor instead of quietly swapping it. */
  const [revision, setRevision] = useState<{ kindChanged: boolean; atSeconds: number } | null>(
    null
  );
  /** Bumped only when a revision changed the verdict, to re-play the badge's entrance. */
  const [revisionSeq, setRevisionSeq] = useState(0);
  /** A property of the origin, not of the run, so it lives outside `ResultState` and outside the
   * probe's Promise.all. Joining that race would let a permissions query that never settles (real
   * in some WebViews) hold the entire verdict hostage. */
  const [capturePermission, setCapturePermission] = useState<CapturePermission>("unknown");
  const prevKind = useRef<LeakAssessment["kind"] | null>(null);
  const running = useRef(false);
  const requestId = useRef(0);
  const mounted = useRef(true);
  const autoStarted = useRef(false);
  const abort = useRef<AbortController | null>(null);
  /* Per-run bookkeeping shared between the first publish and any revision. Refs, not state:
   * `publish` reads them synchronously and must never see a stale render's copy. */
  const compareRun = useRef(false);
  const runBefore = useRef<LeakAssessment["kind"] | null>(null);
  const counted = useRef(false);
  const publishedKind = useRef<LeakAssessment["kind"] | null>(null);
  const publishedAddresses = useRef(0);
  /** A late snapshot that arrived before the first publish had a baseline to judge it against. */
  const lateBuffer = useRef<WebRtcProbeResult | null>(null);
  /* Publishing awaits up to three network round trips, so two publishes for the same run can be
   * in flight at once and the LAST to return wins `setResult` — which, if the earlier one carries
   * the smaller address set, means a revealed leak can be overwritten by the greener reading it
   * was meant to replace. The sequence makes the newest publish authoritative regardless of the
   * order the fetches happen to resolve in. */
  const publishSeq = useRef(0);
  /* Read through a function rather than inline: `run` clears this ref at its start, which is
   * enough for control-flow analysis to decide the later read can only be null. */
  const takeLateBuffer = useCallback((): WebRtcProbeResult | null => {
    const held = lateBuffer.current;
    lateBuffer.current = null;
    return held;
  }, []);
  const reduced = useReducedMotion() ?? false;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // `mounted` alone only silences the callbacks. Without this the peer connection outlives the
      // component by up to 20s (or 10s past a settle), holding sockets open on a page the visitor
      // has already navigated away from.
      abort.current?.abort();
    };
  }, []);

  /** Fire-and-forget, never awaited. Read on mount AND at the top of every run: the exposed copy
   * tells the visitor to revoke access and re-run, so a mount-only read would make that
   * instruction a lie. */
  const refreshCapturePermission = useCallback(() => {
    void readCapturePermission().then((state) => {
      if (mounted.current) setCapturePermission(state);
    });
  }, []);

  useEffect(() => {
    refreshCapturePermission();
  }, [refreshCapturePermission]);

  /* The wait's own clock, so a visitor can see the tool is working rather than hung. Owned here
   * because WebRtcScanning stays hook-free for the server-rendered loading frame. Starts from the
   * slow threshold, since that is when the phase flips. */
  useEffect(() => {
    if (phase !== "slow") return;
    const startedAt = PROBE_SLOW_AFTER_MS;
    setElapsedSeconds(Math.round(startedAt / 1000));
    const id = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1000);
    return () => clearInterval(id);
  }, [phase]);

  /**
   * Turn a probe snapshot into the rendered verdict. Called once when the probe resolves, and
   * again for each late candidate the tail turns up.
   *
   * One path for both on purpose: a revision that recomputed the verdict slightly differently
   * from the first publish would be worse than not revising at all, since the visitor would have
   * no way to tell which reading to trust.
   */
  const publish = useCallback(
    async (
      rawProbe: WebRtcProbeResult,
      rawConnection: IpInfo | null,
      { thisRequest, isRevision }: { thisRequest: number; isRevision: boolean }
    ) => {
      const seq = ++publishSeq.current;
      const stale = () =>
        !mounted.current || thisRequest !== requestId.current || seq !== publishSeq.current;
      const probe = applyDemo(rawProbe);
      const connection = demoMode() === "nobaseline" ? null : rawConnection;

      const allPublicAddresses = probe.addresses.filter((address) => address.kind === "public");
      const baselineCanonical = connection ? canonicalizeIp(connection.ip) : null;
      // Only addresses that actually need an ASN decision get one: an exact match needs no
      // evidence to settle. The location line is a separate, display-only lookup below.
      const mismatched = allPublicAddresses.filter(
        ({ address }) => canonicalizeIp(address) !== baselineCanonical
      );
      const hasMismatch = baselineCanonical !== null && mismatched.length > 0;

      // Exact equality never needs geo or ASN evidence. Publish stable outcomes immediately and
      // let the location data fill in afterwards. A mismatch is different: assessLeak must get
      // its one chance to soften it via ASN before the UI shows either red or amber, and the
      // baseline is re-read so a rotating exit address can't masquerade as a bypass.
      let geo: Record<string, IpInfo> = connection
        ? { [canonicalizeIp(connection.ip)]: connection }
        : {};
      let recheck: { attempted: boolean; ip: string | null } | undefined;
      if (hasMismatch) {
        setPhase("verifying");
        const [enriched, after] = await Promise.all([
          enrichAddresses(connection, mismatched),
          fetchIpInfo("/api/tools/ip/"),
        ]);
        if (stale()) return;
        geo = { ...geo, ...enriched };
        // `attempted` is reported separately from the value: a failed re-read means route
        // stability is UNKNOWN, which must not be mistaken for the route having held still.
        recheck = { attempted: true, ip: after?.ip ?? null };
      }

      // The visitor's exit in the other address family, fetched only when a cross-family extra
      // actually needs settling. Best effort: an IPv4-only line simply won't answer the IPv6
      // endpoint, and the verdict falls back to the ASN comparison.
      const otherFamily =
        hasMismatch && connection
          ? await fetchOtherFamilyBaseline(connection.ipVersion === "v6" ? "ipv6" : "ipv4")
          : null;
      if (stale()) return;

      const enrichedConnection = connection ? (geoFor(geo, connection.ip) ?? connection) : null;
      const assessment = assessWithGeo(probe, enrichedConnection, geo, recheck, otherFamily);

      // The run-over-run delta: closing a leak (or opening one) is the fact the visitor
      // re-ran to learn, so it is computed here and shown beside the verdict. An inconclusive
      // run stays out of it entirely — a timed-out probe after a red result must never
      // announce "exposure closed", and it doesn't become the next comparison baseline either.
      //
      // `before` comes from a ref captured at the START of the run, not from `prevKind`, which
      // this function overwrites. A revision has to be compared against the previous RUN, or a
      // "exposure closed" chip earned by the first publish would survive a revision that just
      // revealed a leak.
      const conclusive = !INCONCLUSIVE_KINDS.has(assessment.kind);
      const before = runBefore.current;
      if (compareRun.current && before !== null && conclusive) {
        // Counted once per run, however many times the run publishes.
        if (!counted.current) {
          counted.current = true;
          setComparisons((n) => n + 1);
        }
        const wasExposed = EXPOSED_KINDS.has(before);
        const isExposed = EXPOSED_KINDS.has(assessment.kind);
        setTransition(
          wasExposed && !isExposed
            ? "closed"
            : !wasExposed && isExposed
              ? "opened"
              : assessment.kind === before
                ? "same"
                : "changed"
        );
      } else {
        setTransition(null);
      }
      if (conclusive) prevKind.current = assessment.kind;

      if (isRevision) {
        const kindChanged =
          publishedKind.current !== null && publishedKind.current !== assessment.kind;
        setRevision({ kindChanged, atSeconds: Math.max(1, Math.round(probe.durationMs / 1000)) });
        // Only a changed verdict re-plays the badge. An extra address that didn't move the
        // needle gets the strip and nothing more.
        if (kindChanged) setRevisionSeq((n) => n + 1);
      }
      publishedKind.current = assessment.kind;
      publishedAddresses.current = probe.addresses.length;

      setResult({
        probe,
        connection: enrichedConnection,
        geo,
        assessment,
        // A mismatch already awaited enrichment above; the fast path has it still in flight.
        geoSettled: hasMismatch,
      });
      if (!isRevision) setRunId((id) => id + 1);

      if (!hasMismatch) {
        // The verdict here is already final. This second pass exists only to put a location
        // under each address, which for a proxy user is how they confirm the exit is where they
        // paid for it to be. On a clean run the WebRTC address IS the connection address, so
        // canonical dedupe collapses this to a single lookup of the visitor's own address.
        //
        // NOT conditional on WebRTC having surfaced an address. It used to be, which meant the
        // better the visitor's protection the less we told them: with WebRTC blocked there are no
        // public candidates, so the connection's own location was never looked up and the panel
        // read "Location unavailable" on exactly the runs that should look most reassuring.
        // With nothing to look up this is a no-op, so the guard bought nothing.
        void enrichAddresses(connection, allPublicAddresses).then((enrichedGeo) => {
          if (stale()) return;
          setResult((current) => {
            if (!current || current.probe !== probe) return current;
            const nextGeo = { ...current.geo, ...enrichedGeo };
            const nextConnection = connection
              ? (geoFor(enrichedGeo, connection.ip) ?? current.connection)
              : null;
            return {
              ...current,
              connection: nextConnection,
              geo: nextGeo,
              assessment: assessWithGeo(probe, nextConnection, nextGeo),
              geoSettled: true,
            };
          });
        });
      }
    },
    []
  );

  const run = useCallback(
    async (compare: boolean) => {
      // `busy` alone can't stop a double click or Strict Mode's effect replay from launching
      // overlapping probes; the ref guard is synchronous.
      if (running.current) return;
      running.current = true;
      refreshCapturePermission();
      const thisRequest = ++requestId.current;
      // A re-run abandons the previous run's tail rather than leaving two connections listening.
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;
      compareRun.current = compare;
      // Captured before anything publishes, so a revision compares against the previous RUN.
      runBefore.current = prevKind.current;
      counted.current = false;
      publishedKind.current = null;
      publishedAddresses.current = 0;
      lateBuffer.current = null;
      if (mounted.current) {
        setBusy(true);
        setPhase("probing");
        setLiveCandidates([]);
        setRevision(null);
      }
      try {
        // The probe and the connection baseline race in parallel: the probe is bounded by its own
        // timeout and never rejects, and a failed baseline degrades to "unverified" rather than
        // sinking the run.
        const [rawProbe, rawConnection] = await Promise.all([
          runWebRtcProbe({
            signal: controller.signal,
            onCandidate: (candidate) => {
              if (mounted.current && thisRequest === requestId.current) {
                setLiveCandidates((prev) => [...prev, candidate]);
              }
            },
            // No verdict yet. Say so on the scanning screen — which is still the screen, because
            // nothing publishes until the probe resolves.
            onSlow: () => {
              if (mounted.current && thisRequest === requestId.current) setPhase("slow");
            },
            // A path that answered after the verdict was published. Revise rather than leave a
            // stale pass on screen: this is what makes settling early safe.
            onLate: (late) => {
              if (!mounted.current || thisRequest !== requestId.current) return;
              // The tail can go quiet before the baseline fetch resolves, so a late snapshot that
              // arrives before the first publish is held rather than assessed against no baseline.
              if (publishedKind.current === null) {
                lateBuffer.current = late;
                return;
              }
              // The probe only calls this when the tail actually turned something up, so there is
              // always something to judge. It used to also fire on a bare end-of-gathering signal,
              // and the "swap the receipts, keep the verdict" branch that handled it replaced the
              // probe object — which silently stranded the deferred location lookup keyed on it,
              // leaving "Location unavailable" on exactly the clean runs meant to reassure.
              void publish(late, rawConnection, { thisRequest, isRevision: true });
            },
          }),
          fetchIpInfo("/api/tools/ip/"),
        ]);
        if (!mounted.current || thisRequest !== requestId.current) return;

        // Dev-only: hold the scanning frame's slow state on screen instead of publishing, so it
        // can be looked at without a hung network.
        if (demoMode() === "slow") {
          if (mounted.current) setPhase("slow");
          return;
        }

        await publish(rawProbe, rawConnection, { thisRequest, isRevision: false });

        const buffered = takeLateBuffer();
        if (buffered && buffered.addresses.length > publishedAddresses.current) {
          await publish(buffered, rawConnection, { thisRequest, isRevision: true });
        }

        // Dev-only: a late leaking address through the real revision path.
        if (demoMode() === "revised") {
          setTimeout(() => {
            if (!mounted.current || thisRequest !== requestId.current) return;
            void publish(demoLateProbe(rawProbe), rawConnection, {
              thisRequest,
              isRevision: true,
            });
          }, 2500);
        }
      } finally {
        if (thisRequest === requestId.current) {
          running.current = false;
          if (mounted.current) setBusy(false);
        }
      }
    },
    [publish, refreshCapturePermission, takeLateBuffer]
  );

  useEffect(() => {
    if (autoStarted.current) return;
    autoStarted.current = true;
    void run(false);
  }, [run]);

  /* Every branch renders the same root with the same first child, so one live region stays
   * mounted from first paint and only its text swaps. */
  const shell = (announcement: string, children: React.ReactNode) => (
    <div className="ph-no-capture flex flex-col gap-5">
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
      {children}
    </div>
  );

  if (!result) {
    // The same frame the route-level loading.tsx painted, with two upgrades only the client can
    // add: candidates stream into the log as the browser emits them, and a wait long enough to
    // notice says so. No verdict is ever rendered from here — that is the point.
    return shell(
      phase === "verifying"
        ? "The addresses differ. Verifying whether they share a network."
        : phase === "slow"
          ? "Still checking. Your browser hasn't signalled that it finished, so we're waiting for late addresses."
          : "Checking whether WebRTC leaks your IP.",
      <WebRtcScanning
        liveCandidates={liveCandidates}
        phase={phase}
        elapsedSeconds={phase === "slow" ? elapsedSeconds : undefined}
      />
    );
  }

  const { probe, connection, geo, assessment, geoSettled } = result;
  const publicCompared = assessment.compared;
  const seconds = (probe.durationMs / 1000).toFixed(1);

  const verdict = VERDICTS[assessment.kind];

  /** The headline as plain text, for the live region and both exports. */
  const verdictSentence = `${verdict.pre}${verdict.em}.`;

  const glyph = GLYPHS[assessment.kind];

  const mdnsAddresses = probe.addresses.filter((a) => a.kind === "mdns");
  /* Exactly the set `assessLeak` treats as exposure, so the footer line can never flag something
   * the verdict called clean. Loopback is deliberately absent: 127.0.0.1 is on every machine
   * ever made, so it narrows nothing down, and the amber "visible to scripts" line used to appear
   * under a green pass claiming it revealed a device. It is still listed in the candidate log,
   * correctly labelled `loopback`, which is the honest place for it. */
  const rawLocal = probe.addresses.filter(
    (a) => a.kind === "private" || a.kind === "linkLocal" || a.kind === "cgnat"
  );

  /* Deliberately NOT derived from `rawLocal`: that set includes CGNAT, which the mDNS masking
   * claim does not cover. See localNetworkNote for why saying otherwise would be
   * wrong, and wrong specifically at mobile-proxy users. */
  const noteKind = localNetworkNote(probe.addresses, capturePermission);
  const exposedNote = noteKind === null || noteKind === "masked-unprompted" ? null : noteKind;
  const noteSentence =
    noteKind === "masked-unprompted" ? MASKED_NOTE_FULL : exposedNote && EXPOSED_NOTES[exposedNote];

  const presented: PresentedResult = {
    ...result,
    presentation: {
      verdict: verdictSentence,
      run: runId,
      comparisons,
      transition,
      localNetworkNote: noteSentence ?? null,
    },
    attribution: {
      ipData: GEO_DATA_ATTRIBUTION.text,
      source: GEO_DATA_ATTRIBUTION.href,
      license: GEO_DATA_ATTRIBUTION.licenseText,
      licenseUrl: GEO_DATA_ATTRIBUTION.licenseHref,
    },
  };
  const reportText = reportToText(presented);

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(presented, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "webrtc-leak-test.json";
    a.hidden = true;
    document.body.append(a);
    a.click();
    a.remove();
    // Safari may not consume the object URL until after the click task returns.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  /* ph-no-capture on the shell keeps every address out of PostHog session replay. */
  return shell(
    busy
      ? "Checking whether WebRTC leaks your IP."
      : // The note is appended for screen-reader parity with the sentence sighted users read.
        // Safe against re-announcing this whole live region only because the permission read
        // resolves in ~1ms, long before `busy` flips false.
        `Test complete. ${verdictSentence} ${verdict.gloss}${exposedNote ? ` ${EXPOSED_NOTES[exposedNote]}` : ""}${revision ? ` ${revisionSentence(revision)}` : ""}`,
    <div className="flex flex-col gap-5">
      <Card className="relative gap-0 overflow-hidden p-0">
        {/* One quiet texture, no light: a fine dot grid fading out before the gloss. The
         * verdict's colour lives in the headline, the seal and the chips, nowhere ambient. */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <div className="absolute inset-x-0 top-0 h-44 bg-[radial-gradient(rgba(255,255,255,0.055)_1px,transparent_1px)] [mask-image:linear-gradient(to_bottom,black,transparent)] bg-[size:14px_14px]" />
        </div>

        <div className="relative flex flex-col gap-6 p-5 sm:p-7">
          {/* Verdict row: the answer, then the controls. Nothing else. */}
          <div className="flex flex-wrap items-start justify-between gap-3">
            <h2 className="min-w-0 text-2xl font-semibold tracking-tight text-white sm:text-[26px]">
              {verdict.pre}
              <span className={TONE_TEXT[verdict.tone]}>{verdict.em}</span>.
            </h2>
            <div className="flex items-center gap-1">
              <CopyButton value={reportText} label="test report" />
              {/* The site's own Button rather than a one-off. This is the only real control on the
               * card, and a bespoke ring-and-radius copy of a button drifts from every other one
               * on the site the moment the design system moves. Filled, like the site's CTAs, so
               * the one thing a visitor can act on looks actionable. `sm` keeps it at the 32px
               * the hand-rolled version occupied, so the header row doesn't move. */}
              <Button variant="default" size="sm" onClick={() => run(true)} disabled={busy}>
                <RefreshCw className={cn(busy && !reduced && "animate-spin")} aria-hidden />
                Re-run
              </Button>
            </div>
          </div>

          {/* The comparison IS the tool: one split panel, two readings side by side, and the
           * verdict badge punched through the shared seam with clear air on both sides. Dims
           * while a re-run is measuring, so the screen never asserts an answer it no longer
           * knows. */}
          {/* The comparison plaque, composed like a scoreboard: the left reading locks to the
           * same vertical line as the verdict headline, the right reading mirrors it against
           * the opposite edge, and the badge holds the centre. The leftover width pools
           * symmetrically around the badge, where it reads as clearance for the centrepiece
           * rather than as misalignment. */}
          <div
            className={cn(
              "grid w-full divide-y divide-white/[0.08] rounded-xl bg-black/25 ring-1 ring-white/[0.07] transition-opacity motion-reduce:transition-none sm:grid-cols-[1fr_auto_1fr] sm:divide-y-0",
              busy && "opacity-40"
            )}
          >
            {/* Left reading: flush with the plaque's start edge, on the same alignment line
             * the headline, the gloss and the footer already share. */}
            <div className="min-w-0 p-4 sm:px-6 sm:py-5">
              <div className="w-full min-w-0">
                <div className="flex items-center gap-2">
                  <span className={READING_ICON}>
                    <Globe className="size-3.5" aria-hidden />
                  </span>
                  <span className={READING_LABEL}>What sites see</span>
                </div>
                {connection ? (
                  <>
                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      <code className="font-mono text-xl leading-tight break-all text-white sm:text-2xl">
                        {connection.ip}
                      </code>
                      <CopyButton value={connection.ip} label="connection address" />
                    </div>
                    <p className="mt-1.5 text-[13px] text-white/60">
                      {geoLine(connection) ?? (geoSettled ? "Location unavailable" : "\u00A0")}
                    </p>
                  </>
                ) : (
                  <p className="mt-2.5 font-mono text-xl text-amber-300 sm:text-2xl">unreachable</p>
                )}
              </div>
            </div>

            {/* The badge column: fixed, equal clearance to both readings. The seam segments
             * descend from the plaque's edges and stop short of the disc. Hidden on mobile,
             * where the stacked halves are separated by the hairline instead.
             *
             * The top segment is fixed at the length it has when both readings are one
             * address line tall (the skeleton's reserved height), so the badge is pinned to
             * the seam rather than centred in it: when a reading grows past the reserved
             * height — a geo line wrapping under a long ISP name, an IPv6 address breaking
             * across lines, a second WebRTC address — the plaque extends downward and the
             * badge holds its position instead of dropping mid-handover. Only the bottom
             * segment stretches. Must match WebRtcScanning's badge column exactly. */}
            <div className="hidden sm:flex sm:flex-col sm:items-center sm:self-stretch sm:px-8">
              <span
                className="mt-4 h-[20.28px] w-px shrink-0 bg-gradient-to-b from-transparent to-white/[0.16]"
                aria-hidden
              />
              <motion.span
                /* Revisions that changed the verdict re-play the entrance, so an answer moving
                 * under the visitor is visible rather than a silent swap. An extra address that
                 * didn't change the verdict leaves the disc alone. */
                key={`${runId}.${revisionSeq}`}
                initial={reduced ? false : { scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                /* Critically damped (damping ≈ 2·√stiffness): the disc rises to full size and
                 * stops. Underdamped values overshoot — at damping 24 the disc measurably grew
                 * to 104.3% and wobbled back over ~half a second, which read as the badge
                 * settling after the skeleton handed over. */
                transition={{ type: "spring", stiffness: 380, damping: 39 }}
                className={cn(
                  "relative my-2 flex size-10 items-center justify-center rounded-full font-mono text-xl font-bold shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] ring-4 ring-black/80 select-none",
                  BADGE_FILL[verdict.tone]
                )}
                aria-hidden
              >
                {/* One sonar ping when the verdict lands, then stillness. */}
                {!reduced && (
                  <motion.span
                    key={`ripple-${runId}`}
                    initial={{ scale: 1, opacity: 0.5 }}
                    animate={{ scale: 2.4, opacity: 0 }}
                    transition={{ duration: 0.9, ease: "easeOut" }}
                    className={cn("absolute inset-0 rounded-full", BADGE_RIPPLE[verdict.tone])}
                  />
                )}
                <span className="relative" style={glyphNudge(glyph)}>
                  {glyph}
                </span>
              </motion.span>
              <span
                className="mb-4 w-px flex-1 bg-gradient-to-b from-white/[0.16] to-transparent"
                aria-hidden
              />
            </div>

            {/* Right reading: the left one mirrored against the plaque's end edge — icon chip
             * outermost, copy control turned inward — so the two readings bookend the badge
             * and the free width splits evenly around it. */}
            <div className="min-w-0 p-4 sm:px-6 sm:py-5">
              <div className="w-full min-w-0">
                <div className="flex items-center gap-2 sm:flex-row-reverse">
                  <span className={READING_ICON}>
                    <Waypoints className="size-3.5" aria-hidden />
                  </span>
                  <span className={READING_LABEL}>What WebRTC reveals</span>
                </div>
                {publicCompared.length > 0 ? (
                  <div className="mt-2.5 flex min-w-0 flex-col gap-3">
                    {publicCompared.map((c) => {
                      const status = addressStatus(c);
                      const info = geoFor(geo, c.address);
                      return (
                        <div key={c.address} className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2 sm:flex-row-reverse">
                            <code
                              className={cn(
                                "font-mono text-xl leading-tight break-all sm:text-2xl",
                                status === "unverified" ? "text-amber-300" : "text-white"
                              )}
                            >
                              {/* Diffed against the connection address: only the segments
                               * that differ light up, so a leak reads digit by digit. */}
                              {connection && (status === "escaped" || status === "sameNetwork") ? (
                                <DiffAddress
                                  address={c.address}
                                  baseline={connection.ip}
                                  tone={status === "escaped" ? "bad" : "warn"}
                                />
                              ) : (
                                c.address
                              )}
                            </code>
                            <CopyButton value={c.address} label="WebRTC address" />
                          </div>
                          <p className="mt-1.5 text-[13px] text-white/60 sm:text-right">
                            {/* Only the states that flag something. A matching address needs no
                             * word: the headline says the browser passed, the badge reads "=",
                             * and nothing on the row is coloured, so there is no colour-only
                             * meaning to caption — just an extra label on the good news. */}
                            {status !== "matches" && (
                              <>
                                <span className={cn("font-medium", STATUS_TONE[status])}>
                                  {STATUS_WORDS[status]}
                                </span>
                                {" · "}
                              </>
                            )}
                            {geoLine(info) ?? (geoSettled ? "Location unavailable" : "\u00A0")}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* `leading-tight` matches the address `<code>` above, so this placeholder sits
                   * in the same 30px line box the scanning frame reserved. Without it the row was
                   * 2px shorter than the skeleton and the plaque twitched at handover.
                   *
                   * "still gathering" is gone: nothing is gathering by the time a verdict renders.
                   * `stun-unreachable` reads "unknown" rather than falling through to "nothing",
                   * which under an amber "couldn't check" headline looked like a clean negative. */
                  <p className="mt-2.5 font-mono text-xl leading-tight text-white/70 sm:text-right sm:text-2xl">
                    {probe.status === "errored"
                      ? "unavailable"
                      : assessment.kind === "silent"
                        ? "blocked"
                        : assessment.kind === "unsupported"
                          ? "unavailable"
                          : assessment.kind === "incomplete" ||
                              assessment.kind === "stun-unreachable"
                            ? "unknown"
                            : "nothing"}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* One sentence. The nuance lives in the chips' tooltips and the exported report. */}
          <p className="max-w-2xl text-sm leading-relaxed text-white/70">
            {verdict.gloss}
            {transition === null &&
              assessment.measurementComplete &&
              " Change your VPN, proxy or browser settings and re-run to compare."}
          </p>

          {/* Incompleteness is its own dimension, not a verdict: an exposure found before the
           * cutoff is still real, and the visitor still needs to know more could be missing.
           * Shown on every unfinished run, including the ones that report an exposure. */}
          {!assessment.measurementComplete && assessment.kind !== "incomplete" && (
            <p className="flex max-w-2xl items-start gap-2 rounded-lg bg-amber-400/[0.07] px-3 py-2.5 text-[13px] leading-relaxed text-amber-200/90 ring-1 ring-amber-400/20">
              <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              <span>
                Your browser hadn&rsquo;t finished looking for addresses when this check ended, so
                there may be more it never showed. What you see above still counts; treat it as a
                floor, not the whole picture.
              </span>
            </p>
          )}

          {/* A path that arrived after the verdict was published. The answer above has already
           * been updated; this says so, because a result that silently rewrites itself while the
           * visitor is reading it is worse than a slower one.
           *
           * Mutually exclusive with the strip above by construction: a revision only exists after
           * a settled publish, and settled means the measurement was conclusive. Icon and words
           * carry the meaning; the tone only reinforces it. */}
          {revision && (
            <p
              className={cn(
                "flex max-w-2xl items-start gap-2 rounded-lg px-3 py-2.5 text-[13px] leading-relaxed ring-1",
                revision.kindChanged
                  ? "bg-amber-400/[0.07] text-amber-200/90 ring-amber-400/20"
                  : "bg-white/[0.04] text-white/70 ring-white/10"
              )}
            >
              {revision.kindChanged ? (
                <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              ) : (
                <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              )}
              <span>{revisionSentence(revision)}</span>
            </p>
          )}

          {/* One footer strip: the local-network reading on the left, provenance and the
           * run-over-run badge on the right. Two clusters, one hairline. The exposure note is
           * grouped INSIDE this block, tight under the reading it explains — at the card's
           * gap-6 rhythm it read as its own unrelated section rather than the caption of the
           * amber line above it. */}
          <div className="flex flex-col gap-2.5 border-t border-white/[0.06] pt-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <span className={HEADER}>Local network</span>
                {rawLocal.length > 0 ? (
                  <HoverTip
                    as="span"
                    /* Worded for the whole set, not just the LAN case: it also carries CGNAT, which
                     * is shared CGNAT space rather than anything of the visitor's, so the
                     * older "these private addresses" phrasing made a claim that was false for
                     * every mobile and Tailscale visitor who saw it. */
                    tip="None of these can be reached from the internet, but any script can read them, which adds detail about your device and how it connects."
                    bubbleClassName="start-0 w-64"
                    className="relative text-[13px] text-amber-300"
                  >
                    {rawLocal.map((a) => a.address).join(", ")} visible to scripts
                  </HoverTip>
                ) : mdnsAddresses.length > 0 ? (
                  <HoverTip
                    as="span"
                    tip={
                      "Your browser replaced your LAN address with a random .local name that it rotates regularly. Sites can't turn it back into the real address." +
                      (noteKind === "masked-unprompted" ? ` ${MASKED_TIP_CLAUSE}` : "")
                    }
                    bubbleClassName="start-0 w-64"
                    className="text-brand-green relative inline-flex items-center gap-1.5 text-[13px]"
                  >
                    <Check className="size-3.5" aria-hidden /> masked behind a random .local name
                  </HoverTip>
                ) : (
                  <span className="text-[13px] text-white/55">nothing revealed</span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className="font-mono text-[9px] font-semibold tracking-widest text-white/45 uppercase">
                  stun · run {String(runId).padStart(2, "0")} ·{" "}
                  {(probe.durationMs / 1000).toFixed(1)}s · {probe.candidates.length} candidate
                  {probe.candidates.length === 1 ? "" : "s"} · probe runs locally
                </span>
                {transition === "closed" && (
                  <span className={cn(TRANSITION_CHIP, "bg-brand-green/15 text-brand-green")}>
                    <Check className="size-3" aria-hidden /> Exposure closed since your last run
                  </span>
                )}
                {transition === "opened" && (
                  <span className={cn(TRANSITION_CHIP, "bg-red-400/15 text-red-300")}>
                    New exposure since your last run
                  </span>
                )}
                {transition === "same" &&
                  (EXPOSED_KINDS.has(assessment.kind) ? (
                    <span className={cn(TRANSITION_CHIP, "bg-amber-400/15 text-amber-300")}>
                      Still exposed, same as your last run
                    </span>
                  ) : (
                    <span className={cn(TRANSITION_CHIP, "bg-brand-green/15 text-brand-green")}>
                      <Check className="size-3" aria-hidden /> Same clean result as your last run
                    </span>
                  ))}
              </div>
            </div>

            {/* The cause behind the reading above, shown only when the visitor can act on it.
             * Inside this wrapper so it stays under `ph-no-capture`: moving it out would place
             * it beside addresses in the DOM outside the replay mask. Static on purpose — the
             * permission resolves in ~1ms against a 0.3-20s probe, so animating it in would
             * buy nothing and cost a layout shift. */}
            {exposedNote && (
              <p className="max-w-2xl text-[13px] leading-relaxed text-white/55">
                {EXPOSED_NOTES[exposedNote]}
              </p>
            )}
          </div>
        </div>
      </Card>

      <Card className="px-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className={HEADER}>Network path log ({probe.candidates.length})</span>
          <div className="flex items-center gap-1.5">
            <CopyButton value={reportText} label="full report" />
            <button
              type="button"
              onClick={downloadJson}
              aria-label="Download as JSON"
              className="focus-visible:ring-brand-green/70 inline-flex size-6 shrink-0 items-center justify-center rounded-md text-white/70 transition-colors outline-none hover:bg-white/[0.06] hover:text-white focus-visible:ring-2"
            >
              <Download className="size-3.5" aria-hidden />
            </button>
          </div>
        </div>
        {/* Each arm owns its whole sentence: a settled run needs the duration in the middle
         * ("settled in 1.1s once…"), and appending a shared "in Xs" tail to it read as
         * "…we kept listening afterwards in 1.1s". */}
        <p className="max-w-3xl text-xs text-white/70">
          Every network path the browser offered while gathering, exactly as a site would receive
          it.{" "}
          {probe.status === "complete"
            ? `Gathering completed in ${seconds}s.`
            : probe.status === "settled"
              ? `Gathering settled in ${seconds}s, once a STUN server had answered and no new path had arrived for a moment. Your browser never signalled the end of gathering, which is normal with a VPN or tunnel interface up, so we reported the answer then and kept listening for late paths.`
              : probe.status === "timeout"
                ? `Gathering was cut off at the timeout after ${seconds}s, so this list may be incomplete.`
                : `Gathering stopped early after ${seconds}s, so this list may be incomplete.`}
        </p>
        {probe.candidates.length > 0 ? (
          <ul className="flex flex-col">
            {probe.candidates.map((c) => (
              <IceCandidateRow key={`${c.raw}-${c.atMs}`} candidate={c} />
            ))}
          </ul>
        ) : (
          <p className="py-6 text-center text-sm text-white/55">
            No candidates. This browser refused to gather any network paths for the probe.
          </p>
        )}
        <details className="group">
          <summary
            className={cn(
              JUMP_LINK,
              "cursor-pointer list-none select-none [&::-webkit-details-marker]:hidden"
            )}
          >
            <span
              aria-hidden
              className="transition-transform group-open:rotate-90 motion-reduce:transition-none"
            >
              ▸
            </span>
            what the types mean
          </summary>
          <ul className="mt-1.5 flex max-w-3xl flex-col gap-1">
            {(["host", "srflx", "prflx", "relay"] as const).map((t) => (
              <li key={t} className="text-[11px] leading-relaxed text-white/55">
                <span className="font-mono font-semibold text-white/70 uppercase">{t}</span>{" "}
                {CANDIDATE_TYPE_HINTS[t]}
              </li>
            ))}
          </ul>
        </details>
        <p className="font-mono text-[9px] tracking-widest text-white/45 uppercase">
          stun servers: {STUN_SERVERS.map((s) => s.replace("stun:", "")).join(" · ")}
        </p>
        {/* Licence obligation, not a credit line: CC-BY-4.0 on the local IP database requires
         * credit AND a link to the licence on any page showing results from it. The credit uses
         * DB-IP's own prescribed wording. Do not reword or remove either link.
         *
         * `nofollow` on both: the licence requires that the links EXIST and point where they
         * point, not that search engines follow them. So the obligation is met in full while no
         * ranking signal is handed to an outbound domain — the standard resolution when a link is
         * compulsory rather than editorial. Dropping the links instead would mean using the data
         * unlicensed, which is not a trade worth making on a tool whose whole claim is that it
         * handles your address more carefully than anyone else. */}
        <p className="font-mono text-[9px] tracking-widest text-white/45 uppercase">
          <a
            href={GEO_DATA_ATTRIBUTION.href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="focus-visible:ring-brand-green/70 rounded-sm underline decoration-white/20 underline-offset-2 transition-colors outline-none hover:text-white/70 focus-visible:ring-2"
          >
            {GEO_DATA_ATTRIBUTION.text}
          </a>{" "}
          ·{" "}
          <a
            href={GEO_DATA_ATTRIBUTION.licenseHref}
            target="_blank"
            rel="noopener noreferrer nofollow license"
            className="focus-visible:ring-brand-green/70 rounded-sm underline decoration-white/20 underline-offset-2 transition-colors outline-none hover:text-white/70 focus-visible:ring-2"
          >
            {GEO_DATA_ATTRIBUTION.licenseText}
          </a>
        </p>
      </Card>
    </div>
  );
}
